import type { PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
export function useCommonTemplatesValueChangeTracker(): CommonTemplatesValueChangeTracker {
    const [changes, setChanges] = useState<Map<CommonTemplatesValueKey, CommonTemplatesValue | undefined>>(new Map());
    return {
        has: (key: CommonTemplatesValueKey) => changes.has(key),
        get: (key: CommonTemplatesValueKey) => {
            if (changes.has(key)) {
                return changes.get(key)?.value;
            }
            throw new Error(`Key "${key}" not found in changed project tenant variables`);
        },
        set: (key: CommonTemplatesValueKey, value: PropertyValueResource | undefined) => {
            const copy = new Map(changes);
            const existingValue = copy.get(key);
            copy.set(key, { value: value, status: existingValue?.status });
            setChanges(copy);
        },
        remove: (key: CommonTemplatesValueKey) => {
            const copy = new Map(changes);
            copy.delete(key);
            setChanges(copy);
        },
        add: (key: CommonTemplatesValueKey, value: PropertyValueResource | undefined) => {
            const copy = new Map(changes);
            copy.set(key, { value, status: "new" });
            setChanges(copy);
        },
        delete: (key: CommonTemplatesValueKey) => {
            const templatesValue = changes.get(key);
            const copy = new Map(changes);
            if (templatesValue?.status === "new") {
                copy.delete(key);
            }
            else {
                copy.set(key, { value: undefined, status: "deleted" });
            }
            setChanges(copy);
        },
        reset: () => setChanges(new Map()),
        values: () => Array.from(changes.entries()).map(([key, value]) => {
            const { setId, templateId, tenantId, guid: id } = extractCommonTemplateValueIds(key);
            return {
                id,
                setId,
                templateId,
                tenantId,
                value: value?.value,
                status: value?.status,
            };
        }),
        hasAny: () => changes.size > 0,
        changedTenants: () => {
            const tenantIds: Set<string> = new Set();
            for (const variableKey of changes.keys()) {
                const { tenantId } = extractCommonTemplateValueIds(variableKey);
                tenantIds.add(tenantId);
            }
            return tenantIds;
        },
        newVariables: (setId: string, templateId: string, tenantId: string) => {
            const filteredMap = new Map<CommonTemplatesValueKey, PropertyValueResource | undefined>();
            for (const [key, templatesValue] of changes.entries()) {
                if (key.startsWith(`${setId};${templateId};${tenantId}`) && templatesValue?.status === "new") {
                    filteredMap.set(key, templatesValue.value);
                }
            }
            return filteredMap;
        },
        removedVariables: () => {
            const filteredMap = new Map<CommonTemplatesValueKey, PropertyValueResource | undefined>();
            for (const [key, templatesValue] of changes.entries()) {
                if (templatesValue?.status === "deleted") {
                    filteredMap.set(key, templatesValue.value);
                }
            }
            return filteredMap;
        },
    };
}
type Status = "new" | "deleted";
interface CommonTemplatesValue {
    value: PropertyValueResource | undefined;
    status?: Status;
}
export interface CommonTemplatesValueChangeTracker {
    has: (key: CommonTemplatesValueKey) => boolean;
    get: (key: CommonTemplatesValueKey) => PropertyValueResource | undefined; // To satisfy type check. Notice that PropertyValueResource can be undefined
    add: (key: CommonTemplatesValueKey, value: PropertyValueResource | undefined) => void;
    set: (key: CommonTemplatesValueKey, value: PropertyValueResource | undefined) => void;
    delete: (key: CommonTemplatesValueKey) => void;
    remove: (key: CommonTemplatesValueKey) => void;
    reset: () => void;
    values: () => ChangedCommonTemplatesValue[];
    hasAny: () => boolean;
    changedTenants: () => Set<string>;
    newVariables: (setId: string, templateId: string, tenantId: string) => Map<CommonTemplatesValueKey, PropertyValueResource | undefined>;
    removedVariables: () => Map<CommonTemplatesValueKey, PropertyValueResource | undefined>;
}
export interface ChangedCommonTemplatesValue {
    id: string;
    setId: string;
    templateId: string;
    tenantId: string;
    value: PropertyValueResource | undefined;
    status: Status | undefined;
}
export type CommonTemplatesValueKey = `${string};${string};${string};${string}`;
export function createCommonTemplatesValueKey(setId: string, templateId: string, tenantId: string, guid: string): CommonTemplatesValueKey {
    return `${setId};${templateId};${tenantId};${guid}`;
}
function extractCommonTemplateValueIds(key: CommonTemplatesValueKey): {
    setId: string;
    templateId: string;
    tenantId: string;
    guid: string;
} {
    const extracted = key.split(`;`);
    return {
        setId: extracted[0],
        templateId: extracted[1],
        tenantId: extracted[2],
        guid: extracted[3],
    };
}
